import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Reveal, Label } from "./motion";
import { TRAINER } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIMES = ["6:00 AM", "7:00 AM", "9:00 AM", "12:00 PM", "4:00 PM", "5:30 PM", "7:00 PM"];
const GOALS = ["Build muscle & strength", "Fat loss", "General fitness & health", "Athletic performance", "Injury rehab / mobility"];

const inputCls = "w-full bg-[#0A0A0A] border-b border-zinc-700 rounded-none px-4 py-3 text-white focus:border-[#FF3B30] focus:outline-none transition-colors placeholder:text-zinc-600";

export const BookingForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", goal: "", message: "" });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/consultations`, {
        ...form,
        preferred_date: date ? format(date, "PPP") : "",
        preferred_time: time,
      });
      toast.success("Request sent! Ricardo will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", goal: "", message: "" });
      setDate(null);
      setTime("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" className="px-4 md:px-8 lg:px-12 py-24 md:py-32 lg:py-48" data-testid="booking-section">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <Reveal><Label>06 — Free Consultation</Label></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.9] mt-6 text-white">
              Let&apos;s work<br /><span className="text-[#FF3B30]">together.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-zinc-400 text-base lg:text-lg mt-8 max-w-md leading-relaxed">
              Book a complimentary, no-obligation consultation. Tell me your goals and pick a time that suits you — I&apos;ll follow up personally.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-12 space-y-4 border-t border-zinc-800 pt-10">
              <a href={`mailto:${TRAINER.email}`} className="block text-zinc-300 hover:text-[#FF3B30] transition-colors" data-testid="contact-email">{TRAINER.email}</a>
              <a href={`tel:${TRAINER.phone}`} className="block text-zinc-300 hover:text-[#FF3B30] transition-colors" data-testid="contact-phone">{TRAINER.phone}</a>
              <p className="text-zinc-500 text-sm">{TRAINER.instagram} · {TRAINER.location}</p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="space-y-6 border border-zinc-800 bg-[#121212] p-8 lg:p-10" data-testid="booking-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2 block">Name *</label>
                <input value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" data-testid="input-name" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2 block">Email *</label>
                <input type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" data-testid="input-email" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2 block">Phone</label>
                <input value={form.phone} onChange={set("phone")} className={inputCls} placeholder="Optional" data-testid="input-phone" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2 block">Primary Goal</label>
                <Select value={form.goal} onValueChange={(v) => setForm((f) => ({ ...f, goal: v }))}>
                  <SelectTrigger className="w-full bg-[#0A0A0A] border-0 border-b border-zinc-700 rounded-none px-4 py-3 h-auto text-white focus:ring-0 focus:border-[#FF3B30]" data-testid="select-goal">
                    <SelectValue placeholder="Select a goal" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-zinc-700 text-white rounded-none">
                    {GOALS.map((g) => (
                      <SelectItem key={g} value={g} className="focus:bg-[#FF3B30] focus:text-white rounded-none cursor-pointer">{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2 block">Preferred Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className={`${inputCls} flex items-center justify-between text-left`} data-testid="date-trigger">
                      <span className={date ? "text-white" : "text-zinc-600"}>{date ? format(date, "PPP") : "Pick a date"}</span>
                      <CalendarIcon size={16} className="text-zinc-500" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#121212] border-zinc-700 rounded-none" align="start">
                    <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus className="text-white" />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2 block">Preferred Time</label>
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger className="w-full bg-[#0A0A0A] border-0 border-b border-zinc-700 rounded-none px-4 py-3 h-auto text-white focus:ring-0 focus:border-[#FF3B30]" data-testid="select-time">
                    <SelectValue placeholder="Pick a time" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-zinc-700 text-white rounded-none">
                    {TIMES.map((t) => (
                      <SelectItem key={t} value={t} className="focus:bg-[#FF3B30] focus:text-white rounded-none cursor-pointer">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2 block">Message</label>
              <textarea value={form.message} onChange={set("message")} rows={3} className={`${inputCls} resize-none`} placeholder="Anything I should know?" data-testid="input-message" />
            </div>

            <button type="submit" disabled={loading} className="w-full bg-[#FF3B30] text-white px-8 py-4 font-bold uppercase tracking-wider hover:bg-[#D6281F] transition-colors flex items-center justify-center gap-2 disabled:opacity-60" data-testid="submit-booking">
              {loading ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : "Request My Free Consult"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
